import { Box, Button, Checkbox, Menu, styled } from "@mui/material";
import clsx from "clsx";
import moment from "moment";
import { MouseEvent, ReactNode, useMemo, useState } from "react";
import TableComponent, {
    ITableColumn,
} from "../../../components/common-table/table-component";
import { displayDateFormat } from "../../../constants";
import { IPermissionBlock } from "../../../features/auth/interface/dto/user-details.dto";
import { IRootState } from "../../../redux/root.reducer";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { columnsList } from "../../../service/list-columns";
import { canAccessLegacyLinks } from "../../../service/user-permission";
import { getListDetailsPath } from "../../../shared/component/get-list-details-path";
import { openLegacyUrl } from "../../../shared/component/open-legacy-url";
import RenderLink from "../../../shared/component/render-link";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import { ListCategory, ListColumns } from "../enum/list-columns";
import { IListDto } from "../interface/dto/lists.dto";
import {
    dataBrowserListSetPaginationAction,
    dataBrowserListSetSortingAction,
    dataBrowserSelectListToggleAction,
} from "../redux/lists.actions";
import { ITEMS_PER_PAGE_ALL } from "../selectors/filters-and-pagination";
import Columns from "./columns";
import ExportButton from "./export-button";
import Filter from "./filter";
import SelectAll from "./selectall";

const CHECKBOX_COLUMN = "checkbox_column";

const iconMap = {
    Columns: "/assets/column-icon.svg",
    Filters: "/assets/filter-icon.svg",
};

type MenuKeys = keyof typeof iconMap;

const generateMenuHeader = (
    label: MenuKeys,
    anchorEl: HTMLElement | null,
    handleClick: (event: MouseEvent<HTMLElement>) => void,
    handleClose: () => void,
    children: ReactNode,
): ReactNode => (
    <Box>
        <Button
            id="basic-button"
            onClick={handleClick}
            className={clsx("menu-label", { highlighted: !!anchorEl })}
        >
            <img src={iconMap[label]} alt={label} />
            {label}
        </Button>
        <StyledMenu
            className={label}
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={handleClose}
            MenuListProps={{
                "aria-labelledby": "basic-button",
            }}
        >
            {children}
        </StyledMenu>
    </Box>
);

const ListsTable = ({ className }: IWithClassName) => {
    const dispatch = useAppDispatch();
    const {
        lists,
        pagination,
        columns,
        isLoading,
        selectAll,
        selectedListIds,
    } = useAppSelector((state) => state.dataBrowserList);

    const user = useAppSelector(({ auth }: IRootState) => auth.user);

    const hasLegacylinkAccess = canAccessLegacyLinks(user?.role);

    const [anchorElColumn, setAnchorElColumn] = useState<null | HTMLElement>(
        null,
    );
    const [anchorElFilter, setAnchorElFilter] = useState<null | HTMLElement>(
        null,
    );

    const tableColumns = useMemo(
        () => [
            {
                title: "",
                id: CHECKBOX_COLUMN,
                sortable: false,
                width: 20,
            },
            ...(columns
                .map((columnKey) => {
                    const column = columnsList.find((c) => c.id === columnKey);
                    return (
                        column && {
                            title: column.name,
                            id: column.id,
                            sortable: true,
                            width: column.width,
                        }
                    );
                })
                .filter((column) => !!column) as Array<ITableColumn>),
        ],
        [columns],
    );

    const handleRowsPerPageChange = (rows: number) => {
        dispatch(
            dataBrowserListSetPaginationAction(pagination.currentPage, rows),
        );
    };

    const handlePageChange = (page: number) => {
        dispatch(
            dataBrowserListSetPaginationAction(page, pagination.itemsPerPage),
        );
    };

    const handleColumnOrder = (
        columnIndex: number,
        column: ITableColumn,
        direction: "asc" | "desc",
    ) => {
        dispatch(
            dataBrowserListSetSortingAction(
                column.id as ListColumns,
                direction,
            ),
        );
    };

    const handleRowSelection = (listId: number) => {
        dispatch(dataBrowserSelectListToggleAction(listId));
    };

    const hasAccessToEntityItem = (
        entityItem: string,
        permission?: IPermissionBlock,
    ): boolean => {
        if (permission) {
            if (permission.exclude?.length) {
                return !permission.exclude.includes(entityItem);
            } else if (permission.include?.length) {
                return permission.include.includes(entityItem);
            }
        }

        return true;
    };

    const getCellContent = (
        list: IListDto,
        columnKey: string,
        rowId: number,
    ): ReactNode => {
        if (columnKey === CHECKBOX_COLUMN) {
            const manuallySelected = selectedListIds.includes(rowId);
            return (
                <Checkbox
                    checked={
                        (selectAll && !manuallySelected) ||
                        (!selectAll && manuallySelected)
                    }
                    onChange={() => handleRowSelection(rowId)}
                    disableFocusRipple={true}
                    disableRipple={true}
                    disableTouchRipple={true}
                />
            );
        } else if (columnKey === ListColumns.NAME && list.hasAccess) {
            return (
                <RenderLink
                    href={getListDetailsPath(
                        list[ListColumns.LIST_ID].toString(),
                    )}
                >
                    {list[columnKey as keyof IListDto]?.toString()}
                </RenderLink>
            );
        } else if (
            [
                ListColumns.HEADERS,
                ListColumns.COUNTRY_CODE,
                ListColumns.COUNTRY_NAME,
            ].includes(columnKey as ListColumns)
        ) {
            const permissionBlock = {
                [ListColumns.HEADERS]: user?.permissions?.field,
                [ListColumns.COUNTRY_CODE]: user?.permissions?.country,
                [ListColumns.COUNTRY_NAME]: user?.permissions?.country,
            }[columnKey];

            if (columnKey === ListColumns.COUNTRY_NAME) {
                return (list.countryNames || []).map((countryName: string) => (
                    <span
                        key={countryName}
                        className={clsx(
                            "array-item",
                            list.hasAccess &&
                                !hasAccessToEntityItem(
                                    countryName,
                                    permissionBlock,
                                ) &&
                                "greyed-out",
                        )}
                    >
                        {countryName}
                    </span>
                ));
            }

            if (columnKey === ListColumns.COUNTRY_CODE) {
                return (list.countryCodes || []).map((countryCode: string) => (
                    <span
                        key={countryCode}
                        className={clsx(
                            "array-item",
                            list.hasAccess &&
                                !hasAccessToEntityItem(
                                    countryCode,
                                    permissionBlock,
                                ) &&
                                "greyed-out",
                        )}
                    >
                        {countryCode}
                    </span>
                ));
            }

            return (list[columnKey as keyof IListDto] as Array<string>).map(
                (entryItem) => (
                    <span
                        key={entryItem}
                        className={clsx(
                            "array-item",
                            list.hasAccess &&
                                !hasAccessToEntityItem(
                                    entryItem,
                                    permissionBlock,
                                ) &&
                                "greyed-out",
                        )}
                    >
                        {entryItem}
                    </span>
                ),
            );
        } else if (columnKey === ListColumns.LAST_UPDATED) {
            const lastUpdated = list[columnKey as keyof IListDto];
            return lastUpdated
                ? moment.utc(lastUpdated?.toString()).format(displayDateFormat)
                : lastUpdated;
        } else {
            return list[columnKey as keyof IListDto]?.toString();
        }
    };

    const renderCell = (rowIndex: number, columnIndex: number): ReactNode => {
        const adjustedRowIndex =
            pagination.itemsPerPage === ITEMS_PER_PAGE_ALL
                ? rowIndex
                : rowIndex % pagination.itemsPerPage;

        const list = lists?.lists?.[adjustedRowIndex];
        if (!list) return null;

        const columnKey = tableColumns[columnIndex].id;
        const rowId = list[ListColumns.LIST_ID];
        const cellContent = getCellContent(list, columnKey, rowId);
        const categoryClosed =
            list.hasAccess &&
            columnKey === ListColumns.CATEGORY &&
            cellContent === ListCategory.CATEGORY_CLOSED;

        return (
            <span
                className={clsx(
                    !list.hasAccess && "greyed-out",
                    categoryClosed && "red-text",
                )}
            >
                {cellContent}
            </span>
        );
    };

    const uniqueRowId = (rowIndex: number): string => `${rowIndex}`;

    const handleFilterClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorElFilter(event.currentTarget);
    };

    const handleColumnClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorElColumn(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorElFilter(null);
        setAnchorElColumn(null);
    };

    return (
        <div className={className}>
            <div className="header">
                {hasLegacylinkAccess && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() =>
                            openLegacyUrl("/lists/browse_by_category")
                        }
                    >
                        Category View
                    </Button>
                )}
                <ExportButton />
            </div>
            <TableComponent
                columns={tableColumns}
                rowsPerPage={pagination.itemsPerPage}
                totalRows={lists?.total ?? 0}
                currentPage={pagination.currentPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                onPageChange={handlePageChange}
                onColumnOrder={handleColumnOrder}
                renderCell={renderCell}
                uniqueRowId={uniqueRowId}
                isLoading={isLoading}
                customHeader={
                    <>
                        <SelectAll />
                        {generateMenuHeader(
                            "Columns",
                            anchorElColumn,
                            handleColumnClick,
                            handleClose,
                            <Columns />,
                        )}
                        {generateMenuHeader(
                            "Filters",
                            anchorElFilter,
                            handleFilterClick,
                            handleClose,
                            <Filter onClear={handleClose} />,
                        )}
                    </>
                }
            />
        </div>
    );
};

const StyledMenu = styled(Menu)(({ theme }: IWithThemeProps) => ({
    "&.Columns": {
        "& .MuiPaper-root": {
            width: theme?.spacing(38),
        },
    },
    "&.Filters": {
        "& .MuiPaper-root": {
            minWidth: theme?.spacing(38),
        },
    },
}));

export default styled(ListsTable)(({ theme }: IWithThemeProps) => ({
    ".menu-label": {
        borderRadius: 0,
        color: theme?.palette.listsTable.labelTextColor,
        fontSize: 16,
        margin: theme?.spacing(0, 2, 0, 2),
        textTransform: "none",
        "& > img": {
            marginRight: theme?.spacing(1),
        },
    },
    ".highlighted": {
        backgroundColor: theme?.palette.dropdown.selectColor,
        borderRadius: "0px",
    },
    ".greyed-out": {
        opacity: 0.3,
    },
    a: {
        textDecoration: "none",
        color: theme?.palette.listsTable.textColor,
    },
    ".header": {
        gap: theme?.spacing(2),
        marginBottom: theme?.spacing(2),
    },
    ".red-text": {
        textTransform: "uppercase",
        fontWeight: 700,
        color: theme?.palette.listsTable.redTextColor,
    },
    ".array-item:not(:last-child):after": {
        content: "\", \"",
    },
}));
